/**
 * Visueller Auto-Q&A-Bedingungseditor (Paket 24).
 *
 * Der Browser bearbeitet direkt den gemeinsamen Ausdrucksbaum. Es gibt kein
 * zweites Formularmodell, keine freien Feldpfade und keine Browser-Auswertung,
 * die vom Motor abweichen könnte.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Braces,
  Check,
  ChevronDown,
  Copy,
  FlaskConical,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  QA_BEDINGUNGEN_HOECHSTENS,
  QA_BEDINGUNGSEBENEN_HOECHSTENS,
  QA_BEDINGUNGSGRUPPEN_HOECHSTENS,
  QA_BEDINGUNGS_FELDER,
  QA_BEDINGUNGS_OPERATOR_LABEL,
  QA_BEDINGUNGS_VERKNUEPFUNGEN,
  QA_BEDINGUNGS_VERKNUEPFUNG_LABEL,
  QA_ZIELART_LABEL,
  beschreibeQaBedingungen,
  dupliziereQaBedingungsknoten,
  entferneQaBedingungsknoten,
  ersetzeQaBedingungsknoten,
  findeQaBedingungsknoten,
  fuegeQaBedingungsknotenHinzu,
  kanonisiereQaBedingungen,
  neueKennung,
  operatorBrauchtWert,
  pruefeQaBedingungen,
  sichereVorgabeFuerQaFeld,
  verschiebeQaBedingungsknoten,
  zaehleQaBedingungsknoten,
  zielartenFuerQaAnlass,
  type QaAnlass,
  type QaBedingungsFeldDefinition,
  type QaBedingungsfeld,
  type QaBedingungsgruppe,
  type QaBedingungsMangel,
  type QaBedingungsknoten,
  type QaBedingungsOperator,
  type QaBedingungsvergleich,
  type QaBedingungsVerknuepfung,
  type QaZielart,
} from "@cubic/shared";
import { Input } from "@/components/Input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { api, grundVon, istNetzfehler } from "@/lib/api";
import { meldungZu } from "@/lib/meldung";
import { cx, focusRing } from "@/lib/utils";

const FELDGRUPPEN: string[] = [
  ...new Set(
    Object.values(QA_BEDINGUNGS_FELDER).map((feld) => feld.gruppe),
  ),
];

type Schnellstart = {
  titel: string;
  erklaerung: string;
  feld: QaBedingungsfeld;
  operator: QaBedingungsOperator;
  wert?: string | number;
};

const SCHNELLSTARTS: readonly Schnellstart[] = [
  {
    titel: "Nur nach main",
    erklaerung: "Der Zielzweig muss exakt main sein.",
    feld: "mr.zielzweig",
    operator: "ist",
    wert: "main",
  },
  {
    titel: "Kein Entwurf",
    erklaerung: "Draft- und WIP-Merge-Requests auslassen.",
    feld: "mr.entwurf",
    operator: "ist_falsch",
  },
  {
    titel: "Nur fehlgeschlagene Läufe",
    erklaerung: "Nur Pipelines mit Zustand failed prüfen.",
    feld: "pipeline.zustand",
    operator: "ist",
    wert: "failed",
  },
  {
    titel: "Nur Tag-Pipelines",
    erklaerung: "Branch-Pipelines bleiben außen vor.",
    feld: "pipeline.ist_tag",
    operator: "ist_wahr",
  },
  {
    titel: "Nur benannte Auslieferungen",
    erklaerung: "Auslieferungen ohne Namen auslassen.",
    feld: "auslieferung.name",
    operator: "ist_nicht_leer",
  },
  {
    titel: "Nur menschliche Änderungen",
    erklaerung: "System, MCP und Metarr lösen nicht aus.",
    feld: "ausloeser.art",
    operator: "ist",
    wert: "person",
  },
];

function standardfeld(zielArten: readonly QaZielart[]): QaBedingungsfeld {
  if (zielArten.length !== 1) return "ziel.art";
  if (zielArten[0] === "merge_request") return "mr.zielzweig";
  if (zielArten[0] === "pipeline") return "pipeline.zustand";
  return "auslieferung.version";
}

function istFeldVerfuegbar(
  feld: QaBedingungsfeld,
  zielArten: readonly QaZielart[],
): boolean {
  return QA_BEDINGUNGS_FELDER[feld].zielArten.some((art) =>
    zielArten.includes(art),
  );
}

function neuerVergleich(
  zielArten: readonly QaZielart[],
): QaBedingungsvergleich {
  return sichereVorgabeFuerQaFeld(standardfeld(zielArten), neueKennung());
}

function neuerSchnellstart(vorlage: Schnellstart): QaBedingungsvergleich {
  const basis = {
    id: neueKennung(),
    art: "vergleich" as const,
    feld: vorlage.feld,
    operator: vorlage.operator,
  };
  return vorlage.wert === undefined ? basis : { ...basis, wert: vorlage.wert };
}

function knotenUmfang(knoten: QaBedingungsknoten): {
  gruppen: number;
  vergleiche: number;
} {
  if (knoten.art === "vergleich") return { gruppen: 0, vergleiche: 1 };
  return zaehleQaBedingungsknoten(knoten);
}

function ersetzeGruppeMitKindern(
  wurzel: QaBedingungsgruppe,
  gruppenId: string,
  kinder: QaBedingungsknoten[],
): QaBedingungsgruppe {
  const gruppe = findeQaBedingungsknoten(wurzel, gruppenId);
  if (!gruppe || gruppe.art !== "gruppe") return wurzel;
  return ersetzeQaBedingungsknoten(wurzel, gruppenId, { ...gruppe, kinder });
}

function formatierterWert(wert: unknown): string {
  if (wert === null || wert === undefined || wert === "") return "leer / nicht vorhanden";
  if (typeof wert === "boolean") return wert ? "wahr" : "falsch";
  if (Array.isArray(wert)) return wert.length > 0 ? wert.join(", ") : "leere Liste";
  return String(wert);
}

function IconAktion({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex size-8 flex-none items-center justify-center rounded-sm text-tinte4 transition-colors hover:bg-papier2 hover:text-tinte disabled:cursor-not-allowed disabled:opacity-25",
        focusRing,
      )}
    >
      {children}
    </button>
  );
}

export type AutoQaBedingungsEditorProps = {
  projektId: string;
  anlass: QaAnlass;
  wert: QaBedingungsgruppe;
  onChange: (wert: QaBedingungsgruppe) => void;
  maengel?: QaBedingungsMangel[];
};

export function AutoQaBedingungsEditor({
  projektId,
  anlass,
  wert,
  onChange,
  maengel: gegebeneMaengel,
}: AutoQaBedingungsEditorProps) {
  const zielArten = zielartenFuerQaAnlass(anlass);
  const maengel =
    gegebeneMaengel ?? pruefeQaBedingungen(wert, zielartenFuerQaAnlass(anlass));
  const zaehler = zaehleQaBedingungsknoten(wert);
  const [loeschfrage, setzeLoeschfrage] = useState<string | null>(null);
  const [fokusId, setzeFokusId] = useState<string | null>(null);

  useEffect(() => {
    if (!fokusId) return;
    const element = document.getElementById(`qa-feld-${fokusId}`);
    element?.focus();
    setzeFokusId(null);
  }, [fokusId, wert]);

  function aendern(naechsterWert: QaBedingungsgruppe): void {
    onChange(kanonisiereQaBedingungen(naechsterWert));
  }

  function hinzufuegen(gruppenId: string): void {
    if (zaehler.vergleiche >= QA_BEDINGUNGEN_HOECHSTENS) return;
    const vergleich = neuerVergleich(zielArten);
    aendern(fuegeQaBedingungsknotenHinzu(wert, gruppenId, vergleich));
    setzeFokusId(vergleich.id);
  }

  function gruppeHinzufuegen(gruppenId: string, tiefe: number): void {
    if (
      tiefe >= QA_BEDINGUNGSEBENEN_HOECHSTENS ||
      zaehler.gruppen >= QA_BEDINGUNGSGRUPPEN_HOECHSTENS ||
      zaehler.vergleiche >= QA_BEDINGUNGEN_HOECHSTENS
    ) {
      return;
    }
    const erstesKind = neuerVergleich(zielArten);
    const gruppe: QaBedingungsgruppe = {
      id: neueKennung(),
      art: "gruppe",
      verknuepfung: "alle",
      kinder: [erstesKind],
    };
    aendern(fuegeQaBedingungsknotenHinzu(wert, gruppenId, gruppe));
    setzeFokusId(erstesKind.id);
  }

  function duplizieren(
    elternId: string,
    knoten: QaBedingungsknoten,
  ): void {
    const umfang = knotenUmfang(knoten);
    if (
      zaehler.vergleiche + umfang.vergleiche > QA_BEDINGUNGEN_HOECHSTENS ||
      zaehler.gruppen + umfang.gruppen > QA_BEDINGUNGSGRUPPEN_HOECHSTENS
    ) {
      return;
    }
    const eltern = findeQaBedingungsknoten(wert, elternId);
    if (!eltern || eltern.art !== "gruppe") return;
    const index = eltern.kinder.findIndex((kind) => kind.id === knoten.id);
    const kopie = dupliziereQaBedingungsknoten(knoten, neueKennung);
    const kinder = [...eltern.kinder];
    kinder.splice(index + 1, 0, kopie);
    aendern(ersetzeGruppeMitKindern(wert, elternId, kinder));
    const ersterVergleich =
      kopie.art === "vergleich"
        ? kopie
        : kopie.kinder.find((kind) => kind.art === "vergleich");
    if (ersterVergleich) setzeFokusId(ersterVergleich.id);
  }

  function loeschen(
    knoten: QaBedingungsknoten,
    fokusDanach: string,
  ): void {
    if (
      knoten.art === "gruppe" &&
      knoten.kinder.length > 0 &&
      loeschfrage !== knoten.id
    ) {
      setzeLoeschfrage(knoten.id);
      return;
    }
    aendern(entferneQaBedingungsknoten(wert, knoten.id));
    setzeLoeschfrage(null);
    requestAnimationFrame(() =>
      document.getElementById(fokusDanach)?.focus(),
    );
  }

  function schnellstart(vorlage: Schnellstart): void {
    if (zaehler.vergleiche >= QA_BEDINGUNGEN_HOECHSTENS) return;
    const vergleich = neuerSchnellstart(vorlage);
    aendern(fuegeQaBedingungsknotenHinzu(wert, wert.id, vergleich));
    setzeFokusId(vergleich.id);
  }

  const passendeSchnellstarts = SCHNELLSTARTS.filter((vorlage) =>
    istFeldVerfuegbar(vorlage.feld, zielArten),
  );

  return (
    <section aria-labelledby="qa-bedingungen-titel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-akzent" aria-hidden="true" />
            <h2
              id="qa-bedingungen-titel"
              className="font-mono text-[12px] font-semibold uppercase tracking-[0.08em] text-tinte"
            >
              Bedingungen
            </h2>
          </div>
          <p className="mt-2 max-w-[64ch] text-[13px] font-light leading-[1.6] text-tinte3">
            Der Anlass startet nur, wenn dieser Ausdruck erfüllt ist. Feld und
            Operator bleiben typisiert; fehlende Werte treffen ausschließlich
            auf „ist leer“.
          </p>
        </div>
        <Mikrolabel grad={11.5}>
          {zaehler.vergleiche}/{QA_BEDINGUNGEN_HOECHSTENS} Bedingungen ·{" "}
          {zaehler.gruppen}/{QA_BEDINGUNGSGRUPPEN_HOECHSTENS} Gruppen
        </Mikrolabel>
      </div>

      {passendeSchnellstarts.length > 0 ? (
        <div className="mt-5">
          <Mikrolabel grad={11.5} ton="laut" betont>
            Schnellstart
          </Mikrolabel>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {passendeSchnellstarts.map((vorlage) => (
              <button
                key={`${vorlage.feld}:${vorlage.operator}`}
                type="button"
                onClick={() => schnellstart(vorlage)}
                disabled={zaehler.vergleiche >= QA_BEDINGUNGEN_HOECHSTENS}
                className={cx(
                  "group min-w-0 rounded-sm border border-linie bg-papier px-3 py-2.5 text-left transition-colors hover:border-akzent/50 hover:bg-papier2 disabled:opacity-40",
                  focusRing,
                )}
              >
                <span className="flex items-center gap-2 text-[12.5px] font-medium text-tinte">
                  <Plus className="size-3.5 text-akzent" aria-hidden="true" />
                  {vorlage.titel}
                </span>
                <span className="mt-1 block text-[11.5px] leading-[1.45] text-tinte4">
                  {vorlage.erklaerung}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {maengel.length > 0 ? (
        <div
          role="alert"
          className="mt-5 border-l-2 border-red-500 bg-red-50 px-4 py-3 dark:bg-red-950/30"
        >
          <div className="flex items-center gap-2 text-[12.5px] font-medium text-tinte">
            <TriangleAlert className="size-4 text-red-600 dark:text-red-400" aria-hidden="true" />
            {maengel.length === 1
              ? "Eine Bedingung braucht Aufmerksamkeit"
              : `${maengel.length} Bedingungen brauchen Aufmerksamkeit`}
          </div>
          <ul className="mt-2 space-y-1 text-[12px] leading-[1.5] text-tinte3">
            {maengel.map((mangel, index) => (
              <li key={`${mangel.knotenId}:${mangel.code}:${index}`}>
                <a
                  href={`#qa-bedingung-${mangel.knotenId}`}
                  className={cx(
                    "underline decoration-1 underline-offset-[3px] hover:text-tinte",
                    focusRing,
                  )}
                >
                  {mangel.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-5">
        <Bedingungsgruppe
          gruppe={wert}
          wurzel={wert}
          elternId={null}
          pfad=""
          tiefe={1}
          position={0}
          geschwister={1}
          zielArten={zielArten}
          maengel={maengel}
          loeschfrage={loeschfrage}
          setzeLoeschfrage={setzeLoeschfrage}
          onChange={aendern}
          onHinzufuegen={hinzufuegen}
          onGruppeHinzufuegen={gruppeHinzufuegen}
          onDuplizieren={duplizieren}
          onLoeschen={loeschen}
          kannGruppeHinzufuegen={
            zaehler.gruppen < QA_BEDINGUNGSGRUPPEN_HOECHSTENS &&
            zaehler.vergleiche < QA_BEDINGUNGEN_HOECHSTENS
          }
          kannVergleichHinzufuegen={
            zaehler.vergleiche < QA_BEDINGUNGEN_HOECHSTENS
          }
          kannDuplizieren={(knoten) => {
            const umfang = knotenUmfang(knoten);
            return (
              zaehler.vergleiche + umfang.vergleiche <=
                QA_BEDINGUNGEN_HOECHSTENS &&
              zaehler.gruppen + umfang.gruppen <=
                QA_BEDINGUNGSGRUPPEN_HOECHSTENS
            );
          }}
        />
      </div>

      <div className="mt-4 border-l-2 border-akzent/50 bg-akzent/[0.045] px-4 py-3">
        <Mikrolabel grad={11.5} ton="laut" betont>
          So liest Cubic den Ausdruck
        </Mikrolabel>
        <p className="mt-1.5 text-[13px] font-light leading-[1.65] text-tinte">
          {wert.kinder.length === 0
            ? "Keine Einschränkung — jeder passende Anlass läuft."
            : `Läuft nur, wenn ${beschreibeQaBedingungen(wert)}.`}
        </p>
      </div>

      <EchtdatenPruefung
        projektId={projektId}
        anlass={anlass}
        bedingungen={wert}
        maengel={maengel}
      />
    </section>
  );
}

type GruppenProps = {
  gruppe: QaBedingungsgruppe;
  wurzel: QaBedingungsgruppe;
  elternId: string | null;
  pfad: string;
  tiefe: number;
  position: number;
  geschwister: number;
  zielArten: readonly QaZielart[];
  maengel: QaBedingungsMangel[];
  loeschfrage: string | null;
  setzeLoeschfrage: (id: string | null) => void;
  onChange: (wert: QaBedingungsgruppe) => void;
  onHinzufuegen: (gruppenId: string) => void;
  onGruppeHinzufuegen: (gruppenId: string, tiefe: number) => void;
  onDuplizieren: (elternId: string, knoten: QaBedingungsknoten) => void;
  onLoeschen: (knoten: QaBedingungsknoten, fokusDanach: string) => void;
  kannGruppeHinzufuegen: boolean;
  kannVergleichHinzufuegen: boolean;
  kannDuplizieren: (knoten: QaBedingungsknoten) => boolean;
};

function Bedingungsgruppe(props: GruppenProps) {
  const {
    gruppe,
    wurzel,
    elternId,
    pfad,
    tiefe,
    position,
    geschwister,
    zielArten,
    maengel,
    loeschfrage,
    setzeLoeschfrage,
    onChange,
    onHinzufuegen,
    onGruppeHinzufuegen,
    onDuplizieren,
    onLoeschen,
    kannGruppeHinzufuegen,
    kannVergleichHinzufuegen,
    kannDuplizieren,
  } = props;
  const istWurzel = elternId === null;
  const gruppenMaengel = maengel.filter((mangel) => mangel.knotenId === gruppe.id);
  const fokusDanach = elternId
    ? `qa-gruppe-${elternId}`
    : `qa-gruppe-${gruppe.id}`;

  function verknuepfungAendern(verknuepfung: QaBedingungsVerknuepfung): void {
    onChange(
      ersetzeQaBedingungsknoten(wurzel, gruppe.id, {
        ...gruppe,
        verknuepfung,
      }),
    );
  }

  function istVerknuepfungGesperrt(
    verknuepfung: QaBedingungsVerknuepfung,
  ): boolean {
    return (
      istWurzel &&
      gruppe.kinder.length === 0 &&
      verknuepfung !== "alle"
    );
  }

  function verknuepfungMitTaste(
    ereignis: React.KeyboardEvent<HTMLButtonElement>,
    aktuell: QaBedingungsVerknuepfung,
  ): void {
    const richtung = ["ArrowRight", "ArrowDown"].includes(ereignis.key)
      ? 1
      : ["ArrowLeft", "ArrowUp"].includes(ereignis.key)
        ? -1
        : 0;
    const moegliche = QA_BEDINGUNGS_VERKNUEPFUNGEN.filter(
      (verknuepfung) => !istVerknuepfungGesperrt(verknuepfung),
    );
    let ziel: QaBedingungsVerknuepfung | undefined;
    if (ereignis.key === "Home") ziel = moegliche[0];
    if (ereignis.key === "End") ziel = moegliche.at(-1);
    if (richtung !== 0) {
      const index = moegliche.indexOf(aktuell);
      ziel = moegliche[(index + richtung + moegliche.length) % moegliche.length];
    }
    if (!ziel || ziel === aktuell) return;
    ereignis.preventDefault();
    verknuepfungAendern(ziel);
    requestAnimationFrame(() =>
      document
        .getElementById(`qa-verknuepfung-${gruppe.id}-${ziel}`)
        ?.focus(),
    );
  }

  return (
    <fieldset
      id={`qa-bedingung-${gruppe.id}`}
      aria-invalid={gruppenMaengel.length > 0 || undefined}
      className={cx(
        "min-w-0 rounded-sm border bg-papier",
        istWurzel ? "border-tinte/25" : "border-linie",
        gruppenMaengel.length > 0 && "border-red-500/60",
      )}
    >
      <legend className="sr-only">
        {istWurzel ? "Bedingungsausdruck" : `Untergruppe ${pfad}`}
      </legend>
      <div
        id={`qa-gruppe-${gruppe.id}`}
        tabIndex={-1}
        className={cx(
          "flex min-w-0 flex-col gap-3 border-b border-linie px-3 py-3 sm:flex-row sm:items-center",
          tiefe > 1 && "bg-papier2/50",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Braces className="size-4 flex-none text-akzent" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-tinte4">
            {istWurzel ? "Ausdruck" : `Gruppe ${pfad}`}
          </span>
        </div>

        <div
          role="radiogroup"
          aria-label={istWurzel ? "Verknüpfung aller Bedingungen" : `Verknüpfung der Gruppe ${pfad}`}
          className="flex min-w-0 flex-wrap rounded-sm border border-linie bg-papier2 p-0.5"
        >
          {QA_BEDINGUNGS_VERKNUEPFUNGEN.map((verknuepfung) => (
            <button
              key={verknuepfung}
              type="button"
              role="radio"
              aria-checked={gruppe.verknuepfung === verknuepfung}
              id={`qa-verknuepfung-${gruppe.id}-${verknuepfung}`}
              tabIndex={gruppe.verknuepfung === verknuepfung ? 0 : -1}
              disabled={istVerknuepfungGesperrt(verknuepfung)}
              title={
                istWurzel &&
                gruppe.kinder.length === 0 &&
                verknuepfung !== "alle"
                  ? "Eine leere Wurzel bedeutet immer: jeder passende Anlass läuft."
                  : undefined
              }
              onClick={() => verknuepfungAendern(verknuepfung)}
              onKeyDown={(ereignis) =>
                verknuepfungMitTaste(ereignis, verknuepfung)
              }
              className={cx(
                "min-h-7 rounded-[2px] px-2 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                gruppe.verknuepfung === verknuepfung
                  ? "bg-papier text-tinte shadow-xs"
                  : "text-tinte4 hover:text-tinte",
                focusRing,
              )}
            >
              {QA_BEDINGUNGS_VERKNUEPFUNG_LABEL[verknuepfung]}
            </button>
          ))}
        </div>

        {!istWurzel && elternId ? (
          <div className="flex items-center gap-0.5 sm:ml-auto">
            <IconAktion
              label={`Gruppe ${pfad} nach oben`}
              onClick={() =>
                onChange(verschiebeQaBedingungsknoten(wurzel, gruppe.id, -1))
              }
              disabled={position === 0}
            >
              <ArrowUp className="size-3.5" aria-hidden="true" />
            </IconAktion>
            <IconAktion
              label={`Gruppe ${pfad} nach unten`}
              onClick={() =>
                onChange(verschiebeQaBedingungsknoten(wurzel, gruppe.id, 1))
              }
              disabled={position === geschwister - 1}
            >
              <ArrowDown className="size-3.5" aria-hidden="true" />
            </IconAktion>
            <IconAktion
              label={`Gruppe ${pfad} duplizieren`}
              onClick={() => onDuplizieren(elternId, gruppe)}
              disabled={!kannDuplizieren(gruppe)}
            >
              <Copy className="size-3.5" aria-hidden="true" />
            </IconAktion>
            <IconAktion
              label={`Gruppe ${pfad} löschen`}
              onClick={() => onLoeschen(gruppe, fokusDanach)}
            >
              <Trash2 className="size-3.5" aria-hidden="true" />
            </IconAktion>
          </div>
        ) : null}
      </div>

      {loeschfrage === gruppe.id && !istWurzel ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 border-b border-red-500/30 bg-red-50 px-4 py-3 text-[12px] text-tinte dark:bg-red-950/30"
        >
          <TriangleAlert className="size-4 text-red-600 dark:text-red-400" aria-hidden="true" />
          <span>
            Diese Gruppe und alle {gruppe.kinder.length} enthaltenen Einträge löschen?
          </span>
          <button
            type="button"
            onClick={() => setzeLoeschfrage(null)}
            className={cx("ml-auto text-tinte3 underline underline-offset-2", focusRing)}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => onLoeschen(gruppe, fokusDanach)}
            className={cx("font-medium text-red-700 underline underline-offset-2 dark:text-red-400", focusRing)}
          >
            Gruppe löschen
          </button>
        </div>
      ) : null}

      <div className={cx("min-w-0", tiefe > 1 && "border-l-2 border-akzent/25")}>
        {gruppe.kinder.length === 0 ? (
          <div className="px-4 py-7 text-center">
            <p className="text-[13px] font-medium text-tinte">
              Keine Einschränkung
            </p>
            <p className="mx-auto mt-1 max-w-[48ch] text-[12px] font-light leading-[1.55] text-tinte4">
              Jeder passende Anlass läuft. Füge eine Bedingung hinzu, um gezielt
              auszuwählen.
            </p>
            <button
              type="button"
              onClick={() => onHinzufuegen(gruppe.id)}
              className={cx(
                "mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-akzent underline decoration-1 underline-offset-[3px]",
                focusRing,
              )}
            >
              <Plus className="size-3.5" aria-hidden="true" />
              Erste Bedingung hinzufügen
            </button>
          </div>
        ) : (
          <div className="divide-y divide-linie">
            {gruppe.kinder.map((kind, index) => {
              const kindPfad = pfad ? `${pfad}.${index + 1}` : String(index + 1);
              if (kind.art === "gruppe") {
                return (
                  <div key={kind.id} className="p-2.5 sm:p-3">
                    <Bedingungsgruppe
                      {...props}
                      gruppe={kind}
                      elternId={gruppe.id}
                      pfad={kindPfad}
                      tiefe={tiefe + 1}
                      position={index}
                      geschwister={gruppe.kinder.length}
                    />
                  </div>
                );
              }
              return (
                <Vergleichszeile
                  key={kind.id}
                  vergleich={kind}
                  wurzel={wurzel}
                  elternId={gruppe.id}
                  pfad={kindPfad}
                  position={index}
                  geschwister={gruppe.kinder.length}
                  zielArten={zielArten}
                  maengel={maengel.filter((mangel) => mangel.knotenId === kind.id)}
                  onChange={onChange}
                  onDuplizieren={onDuplizieren}
                  onLoeschen={onLoeschen}
                  kannDuplizieren={kannDuplizieren(kind)}
                />
              );
            })}
          </div>
        )}
      </div>

      {gruppe.kinder.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-linie px-4 py-3">
          <button
            type="button"
            onClick={() => onHinzufuegen(gruppe.id)}
            disabled={!kannVergleichHinzufuegen}
            className={cx(
              "inline-flex items-center gap-1.5 text-[12px] font-medium text-akzent disabled:opacity-35",
              focusRing,
            )}
          >
            <Plus className="size-3.5" aria-hidden="true" />
            Bedingung
          </button>
          {tiefe < QA_BEDINGUNGSEBENEN_HOECHSTENS ? (
            <button
              type="button"
              onClick={() => onGruppeHinzufuegen(gruppe.id, tiefe)}
              disabled={!kannGruppeHinzufuegen}
              className={cx(
                "inline-flex items-center gap-1.5 text-[12px] text-tinte3 disabled:opacity-35",
                focusRing,
              )}
            >
              <Braces className="size-3.5" aria-hidden="true" />
              Untergruppe
            </button>
          ) : (
            <Mikrolabel grad={10.5}>Maximale Gruppentiefe erreicht</Mikrolabel>
          )}
        </div>
      ) : null}

      {gruppenMaengel.length > 0 ? (
        <ul className="border-t border-red-500/20 bg-red-50 px-4 py-2 text-[11.5px] text-red-700 dark:bg-red-950/30 dark:text-red-400">
          {gruppenMaengel.map((mangel, index) => (
            <li key={`${mangel.code}:${index}`}>{mangel.text}</li>
          ))}
        </ul>
      ) : null}
    </fieldset>
  );
}

function Vergleichszeile({
  vergleich,
  wurzel,
  elternId,
  pfad,
  position,
  geschwister,
  zielArten,
  maengel,
  onChange,
  onDuplizieren,
  onLoeschen,
  kannDuplizieren,
}: {
  vergleich: QaBedingungsvergleich;
  wurzel: QaBedingungsgruppe;
  elternId: string;
  pfad: string;
  position: number;
  geschwister: number;
  zielArten: readonly QaZielart[];
  maengel: QaBedingungsMangel[];
  onChange: (wert: QaBedingungsgruppe) => void;
  onDuplizieren: (elternId: string, knoten: QaBedingungsknoten) => void;
  onLoeschen: (knoten: QaBedingungsknoten, fokusDanach: string) => void;
  kannDuplizieren: boolean;
}) {
  const definition = QA_BEDINGUNGS_FELDER[vergleich.feld];
  const ungueltig = maengel.length > 0;
  const feldVerfuegbar = istFeldVerfuegbar(vergleich.feld, zielArten);

  function ersetzen(ersatz: QaBedingungsvergleich): void {
    onChange(ersetzeQaBedingungsknoten(wurzel, vergleich.id, ersatz));
  }

  function feldAendern(feld: QaBedingungsfeld): void {
    const neueDefinition = QA_BEDINGUNGS_FELDER[feld];
    const alterOperator = vergleich.operator;
    const operator = (
      neueDefinition.operatoren as readonly QaBedingungsOperator[]
    ).includes(alterOperator)
      ? alterOperator
      : (neueDefinition.operatoren[0] ?? "ist");
    const vorgabe = sichereVorgabeFuerQaFeld(feld, vergleich.id);
    if (!operatorBrauchtWert(operator)) {
      ersetzen({ id: vergleich.id, art: "vergleich", feld, operator });
      return;
    }
    const alterWert = vergleich.wert;
    const wertPasst =
      neueDefinition.datentyp === "zahl"
        ? typeof alterWert === "number" && Number.isFinite(alterWert)
        : typeof alterWert === "string" &&
          (neueDefinition.datentyp !== "auswahl" ||
            neueDefinition.freieAuswahl !== false ||
            neueDefinition.auswahl?.some(
              (option) => option.wert === alterWert,
            ) === true);
    ersetzen({
      id: vergleich.id,
      art: "vergleich",
      feld,
      operator,
      wert: wertPasst ? alterWert : (vorgabe.wert ?? ""),
    });
  }

  function operatorAendern(operator: QaBedingungsOperator): void {
    if (!operatorBrauchtWert(operator)) {
      ersetzen({
        id: vergleich.id,
        art: "vergleich",
        feld: vergleich.feld,
        operator,
      });
      return;
    }
    const vorgabe = sichereVorgabeFuerQaFeld(vergleich.feld, vergleich.id);
    const wert =
      vergleich.wert !== undefined ? vergleich.wert : (vorgabe.wert ?? "");
    ersetzen({ ...vergleich, operator, wert });
  }

  return (
    <div
      id={`qa-bedingung-${vergleich.id}`}
      className={cx(
        "min-w-0 px-3 py-4 sm:px-4",
        ungueltig && "bg-red-50/50 dark:bg-red-950/20",
      )}
    >
      <div className="grid min-w-0 gap-2.5 lg:grid-cols-[34px_minmax(160px,1.25fr)_minmax(145px,1fr)_minmax(145px,1fr)_auto] lg:items-start">
        <div className="flex min-h-9 items-center font-mono text-[11px] tabular-nums text-tinte4">
          {pfad}
        </div>

        <label className="min-w-0">
          <span className="sr-only">Feld der Bedingung {pfad}</span>
          <Select
            value={vergleich.feld}
            onValueChange={(feld) => feldAendern(feld as QaBedingungsfeld)}
          >
            <SelectTrigger
              id={`qa-feld-${vergleich.id}`}
              aria-invalid={!feldVerfuegbar || undefined}
              aria-label={`Feld der Bedingung ${pfad}`}
              hasError={!feldVerfuegbar}
              className="min-w-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FELDGRUPPEN.map((gruppe) => (
                <SelectGroup key={gruppe}>
                  <SelectGroupLabel>{gruppe}</SelectGroupLabel>
                  {(
                    Object.keys(QA_BEDINGUNGS_FELDER) as QaBedingungsfeld[]
                  )
                    .filter(
                      (feld) => QA_BEDINGUNGS_FELDER[feld].gruppe === gruppe,
                    )
                    .map((feld) => {
                      const verfuegbar = istFeldVerfuegbar(feld, zielArten);
                      return (
                        <SelectItem key={feld} value={feld} disabled={!verfuegbar}>
                          {QA_BEDINGUNGS_FELDER[feld].label}
                          {!verfuegbar ? " · nicht verfügbar" : ""}
                        </SelectItem>
                      );
                    })}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <span className="mt-1 block text-[10.5px] leading-[1.4] text-tinte4">
            {definition.beschreibung}
          </span>
        </label>

        <label className="min-w-0">
          <span className="sr-only">Operator der Bedingung {pfad}</span>
          <Select
            value={vergleich.operator}
            onValueChange={(operator) =>
              operatorAendern(operator as QaBedingungsOperator)
            }
          >
            <SelectTrigger
              aria-label={`Operator der Bedingung ${pfad}`}
              aria-invalid={ungueltig || undefined}
              hasError={maengel.some((mangel) => mangel.code === "operator_passt_nicht")}
              className="min-w-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(definition.operatoren as readonly QaBedingungsOperator[]).map(
                (operator) => (
                  <SelectItem key={operator} value={operator}>
                    {QA_BEDINGUNGS_OPERATOR_LABEL[operator]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </label>

        <Werteditor
          vergleich={vergleich}
          pfad={pfad}
          ungueltig={maengel.some((mangel) =>
            ["wert_fehlt", "wert_passt_nicht"].includes(mangel.code),
          )}
          onChange={ersetzen}
        />

        <div className="flex min-h-9 items-center justify-end gap-0.5 lg:justify-start">
          <IconAktion
            label={`Bedingung ${pfad} nach oben`}
            onClick={() =>
              onChange(verschiebeQaBedingungsknoten(wurzel, vergleich.id, -1))
            }
            disabled={position === 0}
          >
            <ArrowUp className="size-3.5" aria-hidden="true" />
          </IconAktion>
          <IconAktion
            label={`Bedingung ${pfad} nach unten`}
            onClick={() =>
              onChange(verschiebeQaBedingungsknoten(wurzel, vergleich.id, 1))
            }
            disabled={position === geschwister - 1}
          >
            <ArrowDown className="size-3.5" aria-hidden="true" />
          </IconAktion>
          <IconAktion
            label={`Bedingung ${pfad} duplizieren`}
            onClick={() => onDuplizieren(elternId, vergleich)}
            disabled={!kannDuplizieren}
          >
            <Copy className="size-3.5" aria-hidden="true" />
          </IconAktion>
          <IconAktion
            label={`Bedingung ${pfad} löschen`}
            onClick={() => onLoeschen(vergleich, `qa-gruppe-${elternId}`)}
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </IconAktion>
        </div>
      </div>

      {maengel.length > 0 ? (
        <ul className="mt-2 pl-0 text-[11.5px] leading-[1.45] text-red-700 dark:text-red-400 lg:pl-[44px]">
          {maengel.map((mangel, index) => (
            <li key={`${mangel.code}:${index}`}>{mangel.text}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Werteditor({
  vergleich,
  pfad,
  ungueltig,
  onChange,
}: {
  vergleich: QaBedingungsvergleich;
  pfad: string;
  ungueltig: boolean;
  onChange: (wert: QaBedingungsvergleich) => void;
}) {
  const definition: QaBedingungsFeldDefinition =
    QA_BEDINGUNGS_FELDER[vergleich.feld];
  if (!operatorBrauchtWert(vergleich.operator)) {
    return (
      <div className="flex min-h-9 items-center rounded-sm border border-dashed border-linie px-3 text-[11px] text-tinte4">
        Kein Vergleichswert nötig
      </div>
    );
  }

  const wert = vergleich.wert;
  if (
    definition.datentyp === "auswahl" &&
    definition.auswahl &&
    definition.freieAuswahl === false
  ) {
    return (
      <label className="min-w-0">
        <span className="sr-only">Wert der Bedingung {pfad}</span>
        <Select
          value={typeof wert === "string" ? wert : ""}
          onValueChange={(naechsterWert) =>
            onChange({ ...vergleich, wert: naechsterWert })
          }
        >
          <SelectTrigger
            aria-label={`Wert der Bedingung ${pfad}`}
            aria-invalid={ungueltig || undefined}
            hasError={ungueltig}
          >
            <SelectValue placeholder="Wert wählen …" />
          </SelectTrigger>
          <SelectContent>
            {definition.auswahl.map((option) => (
              <SelectItem key={option.wert} value={option.wert}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
    );
  }

  const listenId = definition.auswahl
    ? `qa-wert-vorschlaege-${vergleich.id}`
    : undefined;
  return (
    <label className="min-w-0">
      <span className="sr-only">Wert der Bedingung {pfad}</span>
      <Input
        type={definition.datentyp === "zahl" ? "number" : "text"}
        value={
          definition.datentyp === "zahl"
            ? typeof wert === "number"
              ? wert
              : ""
            : typeof wert === "string"
              ? wert
              : ""
        }
        list={listenId}
        maxLength={definition.datentyp === "zahl" ? undefined : 500}
        hasError={ungueltig}
        aria-invalid={ungueltig || undefined}
        placeholder={
          definition.datentyp === "zahl"
            ? "Zahl"
            : definition.datentyp === "textliste"
              ? "Feldname"
              : "Vergleichswert"
        }
        onChange={(ereignis) => {
          if (definition.datentyp === "zahl" && ereignis.target.value === "") {
            const { wert: _wert, ...ohneWert } = vergleich;
            onChange(ohneWert);
            return;
          }
          onChange({
            ...vergleich,
            wert:
              definition.datentyp === "zahl"
                ? Number(ereignis.target.value)
                : ereignis.target.value,
          });
        }}
      />
      {definition.auswahl ? (
        <datalist id={listenId}>
          {definition.auswahl.map((option) => (
            <option key={option.wert} value={option.wert}>
              {option.label}
            </option>
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

function EchtdatenPruefung({
  projektId,
  anlass,
  bedingungen,
  maengel,
}: {
  projektId: string;
  anlass: QaAnlass;
  bedingungen: QaBedingungsgruppe;
  maengel: QaBedingungsMangel[];
}) {
  const moeglicheZielarten = useMemo(
    () => zielartenFuerQaAnlass(anlass),
    [anlass],
  );
  const [offen, setzeOffen] = useState(false);
  const [zielArt, setzeZielArt] = useState<QaZielart>(moeglicheZielarten[0]!);
  const [zielId, setzeZielId] = useState("");
  const [gepruefterStand, setzeGeprueftenStand] = useState<string | null>(null);
  const pruefSignatur = JSON.stringify({
    projektId,
    anlass,
    zielArt,
    zielId,
    bedingungen,
  });

  useEffect(() => {
    if (!moeglicheZielarten.includes(zielArt)) {
      setzeZielArt(moeglicheZielarten[0]!);
      setzeZielId("");
    }
  }, [anlass, moeglicheZielarten, zielArt]);

  const mrs = useQuery({
    queryKey: ["cubic", "auto-qa", "bedingungsziele", projektId, "merge_request"],
    queryFn: () => api.gitlab.mergeRequests.query({ projektId, grenze: 50 }),
    enabled: offen && Boolean(projektId) && zielArt === "merge_request",
  });
  const pipelines = useQuery({
    queryKey: ["cubic", "auto-qa", "bedingungsziele", projektId, "pipeline"],
    queryFn: () => api.gitlab.pipelines.query({ projektId, grenze: 50 }),
    enabled: offen && Boolean(projektId) && zielArt === "pipeline",
  });
  const auslieferungen = useQuery({
    queryKey: ["cubic", "auto-qa", "bedingungsziele", projektId, "auslieferung"],
    queryFn: () => api.gitlab.auslieferungen.query({ projektId, grenze: 50 }),
    enabled: offen && Boolean(projektId) && zielArt === "auslieferung",
  });

  const zielOptionen = useMemo(() => {
    if (zielArt === "merge_request") {
      return (mrs.data?.eintraege ?? []).map((mr) => ({
        id: mr.id,
        label: `!${mr.gitlabIid} · ${mr.titel}`,
        meta: `${mr.quellBranch} → ${mr.zielBranch}`,
      }));
    }
    if (zielArt === "pipeline") {
      return (pipelines.data?.eintraege ?? []).map((pipeline) => ({
        id: pipeline.id,
        label: `#${pipeline.gitlabId} · ${pipeline.gitlabRef}`,
        meta: pipeline.zustand,
      }));
    }
    return (auslieferungen.data?.eintraege ?? []).map((auslieferung) => ({
      id: auslieferung.id,
      label: auslieferung.name,
      meta: auslieferung.version ?? "ohne Version",
    }));
  }, [auslieferungen.data, mrs.data, pipelines.data, zielArt]);

  useEffect(() => {
    if (zielId && !zielOptionen.some((ziel) => ziel.id === zielId)) {
      setzeZielId("");
    }
  }, [zielId, zielOptionen]);

  const pruefen = useMutation({
    mutationFn: (eingabe: {
      signatur: string;
      projektId: string;
      anlass: QaAnlass;
      zielArt: QaZielart;
      zielId: string;
      bedingungen: QaBedingungsgruppe;
    }) =>
      api.autoQa.bedingungenPruefen.mutate({
        projektId: eingabe.projektId,
        anlass: eingabe.anlass,
        zielArt: eingabe.zielArt,
        zielId: eingabe.zielId,
        bedingungen: eingabe.bedingungen,
      }),
    onSuccess: (_antwort, eingabe) => setzeGeprueftenStand(eingabe.signatur),
  });

  const aktiveListe =
    zielArt === "merge_request"
      ? mrs
      : zielArt === "pipeline"
        ? pipelines
        : auslieferungen;
  const listenLaden = aktiveListe.isLoading;
  const listenFehler = aktiveListe.error;
  const veraltet = Boolean(pruefen.data) && gepruefterStand !== pruefSignatur;

  return (
    <div className="mt-4 overflow-hidden rounded-sm border border-linie bg-papier">
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => setzeOffen((alt) => !alt)}
        className={cx(
          "flex w-full items-center gap-3 px-4 py-3 text-left",
          focusRing,
        )}
      >
        <FlaskConical className="size-4 flex-none text-akzent" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-medium text-tinte">
            Mit Projektdaten prüfen
          </span>
          <span className="mt-0.5 block text-[11.5px] font-light text-tinte4">
            Bewertet den Ausdruck am echten Ziel — ohne Auftrag und ohne Änderung.
          </span>
        </span>
        <ChevronDown
          className={cx(
            "size-4 flex-none text-tinte4 transition-transform motion-reduce:transition-none",
            offen && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {offen ? (
        <div className="border-t border-linie px-4 py-4">
          {!projektId ? (
            <p className="text-[12px] text-tinte4">
              Wähle zuerst ein Projekt, damit echte Ziele geladen werden können.
            </p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)_auto] sm:items-end">
                <label className="min-w-0">
                  <span className="mb-1.5 block text-[11px] text-tinte4">Zielart</span>
                  <Select
                    value={zielArt}
                    onValueChange={(art) => {
                      setzeZielArt(art as QaZielart);
                      setzeZielId("");
                    }}
                    disabled={moeglicheZielarten.length === 1}
                  >
                    <SelectTrigger aria-label="Zielart für die Bedingungsprüfung">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moeglicheZielarten.map((art) => (
                        <SelectItem key={art} value={art}>
                          {QA_ZIELART_LABEL[art]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>

                <label className="min-w-0">
                  <span className="mb-1.5 block text-[11px] text-tinte4">Konkretes Ziel</span>
                  <Select value={zielId} onValueChange={setzeZielId} disabled={listenLaden}>
                    <SelectTrigger aria-label="Ziel für die Bedingungsprüfung">
                      <SelectValue
                        placeholder={listenLaden ? "Lade Ziele …" : "Ziel wählen …"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {zielOptionen.map((ziel) => (
                        <SelectItem key={ziel.id} value={ziel.id}>
                          {ziel.label} · {ziel.meta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>

                <button
                  type="button"
                  onClick={() =>
                    pruefen.mutate({
                      signatur: pruefSignatur,
                      projektId,
                      anlass,
                      zielArt,
                      zielId,
                      bedingungen,
                    })
                  }
                  disabled={
                    !zielId || maengel.length > 0 || pruefen.isPending || listenLaden
                  }
                  className={cx(
                    "inline-flex min-h-9 items-center justify-center gap-2 bg-tinte px-4 text-[12px] font-medium text-papier disabled:opacity-35",
                    focusRing,
                  )}
                >
                  <FlaskConical className="size-3.5" aria-hidden="true" />
                  {pruefen.isPending ? "Prüft …" : "Jetzt prüfen"}
                </button>
              </div>

              {!listenLaden && !listenFehler && zielOptionen.length === 0 ? (
                <p className="mt-3 text-[11.5px] text-tinte4">
                  Für diese Zielart sind im Projekt noch keine synchronisierten Daten vorhanden.
                </p>
              ) : null}
              {listenFehler ? (
                <p role="alert" className="mt-3 text-[11.5px] text-red-700 dark:text-red-400">
                  Ziele konnten nicht geladen werden.
                </p>
              ) : null}
              {maengel.length > 0 ? (
                <p className="mt-3 text-[11.5px] text-red-700 dark:text-red-400">
                  Behebe zuerst die Bedingungsfehler; geprüft wird nur ein gültiger Ausdruck.
                </p>
              ) : null}
              {pruefen.error ? (
                <p role="alert" className="mt-3 text-[11.5px] text-red-700 dark:text-red-400">
                  {
                    meldungZu(grundVon(pruefen.error), {
                      netzfehler: istNetzfehler(pruefen.error),
                      fallback: "Die Bedingungsprüfung ist fehlgeschlagen.",
                    }).text
                  }
                </p>
              ) : null}

              {pruefen.data ? (
                <div
                  aria-live="polite"
                  className={cx(
                    "mt-4 border-l-2 px-4 py-3",
                    pruefen.data.auswertung.erfuellt
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                      : "border-red-500 bg-red-50 dark:bg-red-950/30",
                    veraltet && "opacity-60",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {pruefen.data.auswertung.erfuellt ? (
                      <Check className="size-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                    ) : (
                      <X className="size-4 text-red-600 dark:text-red-400" aria-hidden="true" />
                    )}
                    <span className="text-[12.5px] font-medium text-tinte">
                      {pruefen.data.auswertung.erfuellt
                        ? "Erfüllt — die Automation würde starten"
                        : "Nicht erfüllt — die Automation würde nicht starten"}
                    </span>
                    {veraltet ? (
                      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-tinte4">
                        Ergebnis veraltet
                      </span>
                    ) : null}
                  </div>
                  <Auswertungsbaum auswertung={pruefen.data.auswertung} tiefe={0} />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

type SichtbareAuswertung = {
  id: string;
  art: "gruppe" | "vergleich";
  erfuellt: boolean;
  text: string;
  aktuellerWert?: unknown;
  kinder?: SichtbareAuswertung[];
};

function Auswertungsbaum({
  auswertung,
  tiefe,
}: {
  auswertung: SichtbareAuswertung;
  tiefe: number;
}) {
  return (
    <div className={cx("mt-2", tiefe > 0 && "border-l border-linie pl-3")}>
      <div className="flex min-w-0 items-start gap-2 py-1 text-[11.5px] leading-[1.45]">
        {auswertung.erfuellt ? (
          <Check className="mt-0.5 size-3.5 flex-none text-emerald-600 dark:text-emerald-400" aria-label="Erfüllt" />
        ) : (
          <X className="mt-0.5 size-3.5 flex-none text-red-600 dark:text-red-400" aria-label="Nicht erfüllt" />
        )}
        <span className="min-w-0 text-tinte3">
          {auswertung.text}
          {auswertung.art === "vergleich" ? (
            <span className="ml-1 text-tinte4">
              — gelesen: {formatierterWert(auswertung.aktuellerWert)}
            </span>
          ) : null}
        </span>
      </div>
      {auswertung.art === "gruppe"
        ? (auswertung.kinder ?? []).map((kind) => (
            <Auswertungsbaum key={kind.id} auswertung={kind} tiefe={tiefe + 1} />
          ))
        : null}
    </div>
  );
}
